import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { getBillTo, GetBillToApiParameter } from "@insite/client-framework/Services/CustomersService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DynamicDropdown, { OptionObject } from "@insite/mobius/DynamicDropdown";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import Toaster from "@insite/mobius/Toast/Toaster";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import PhoneNumberField, { PhoneNumberState } from "@preferredMedical/Components/Fields/PhoneNumberField";
import { EventEx } from "@preferredMedical/Models/Misc/EventEx";
import { PumpRentalEmailRequest } from "@preferredMedical/Models/ProductDetails/PumpRentalEmailRequest";
import { sendPumpRentalEmail } from "@preferredMedical/Services/ProductService";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface PumpRentalProps {
    emailRecipients: string;
    shippingMethods: string;
    title: string;
    description: string;
    additionalChargesLabel: string;
    requiredFieldsLabel: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
});

type OwnProps = PumpRentalProps & ReturnType<typeof mapStateToProps> & HasProduct;

export interface PumpRentalStyles {
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    oneLinerGridItem?: GridItemProps;
    twoLinerGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
}

export const pumpRentalStyles: PumpRentalStyles = {
    modal: {
        size: 800,
        cssOverrides: {
            modalTitle: css`
                border-bottom: 0;
                padding-bottom: 0px;
                justify-content: center;
            `,
            titleButton: css`
                display: none;
            `,
            modalContent: css`
                padding: 20px 30px;
            `,
        },
    },
    container: { gap: 10 },
    oneLinerGridItem: {
        width: 12,
        css: css`
            padding-left: 10px;
            justify-content: center;
        `,
    },
    twoLinerGridItem: {
        width: 6,
        css: css`
            padding-left: 10px;
        `,
    },
    submitButton: {
        css: css`
            width: 100%;
        `,
    },
};

interface PumpRentalState {
    styles: PumpRentalStyles;
    isAuthenticated: boolean;

    contactName: string;
    contactEmail: string;
    phoneNumber: string;
    companyName: string;
    shipToAddress: string;
    accountNumber: string;
    poNumber: string;
    noPORequired: boolean;
    poNumberError: string;
    pumpNumber: number;
    shippingMethod: string;
    rentalPricingBeforeShipping: string;
    needAccessories: string;
    additionalComments: string;
    contactNumberOnChange: EventEx;
    contactNumberUpdate: EventEx;

    contactNameError: string;
    contactEmailError: string;
    phoneNumberError: string;
    companyNameError: string;
    shipToAddressError: string;
    pumpNumberError: string;
    needAccessoriesError: string;
    shippingMethodError: string;

    modalIsOpen: boolean;
    inProgress: boolean;
    shipToAddresses: OptionObject[];
    booleanDropdownOptions: OptionObject[];
    shippingMethodsOptions: OptionObject[];

    billToId?: string;
}

type OwnState = OwnProps & ReturnType<typeof mapStateToProps> & PumpRentalState & HasProduct;

const phoneRegex = new RegExp("[0-9]{3}-[0-9]{3}-[0-9]{4}");

class PumpRentalModal extends React.Component<OwnProps, OwnState> {
    constructor(props: OwnProps) {
        super(props);
        this.state = {
            additionalChargesLabel: props.additionalChargesLabel,
            description: props.description,
            emailRecipients: props.emailRecipients,
            requiredFieldsLabel: props.requiredFieldsLabel,
            shippingMethods: props.shippingMethods,
            title: props.title,
            styles: pumpRentalStyles,
            idx: props.idx,
            product: props.product,
            productInfo: props.productInfo,
            onQtyOrderedChanged: props.onQtyOrderedChanged,
            onUnitOfMeasureChanged: props.onUnitOfMeasureChanged,

            session: props.session,

            isAuthenticated: (props.session.isAuthenticated || props.session.rememberMe) && !props.session.isGuest,

            contactName: "",
            contactEmail: "",
            phoneNumber: "",
            companyName: "",
            shipToAddress: "",
            accountNumber: "",
            poNumber: "",
            noPORequired: false,
            poNumberError: "",
            pumpNumber: 0,
            shippingMethod: "",
            rentalPricingBeforeShipping: "",
            needAccessories: "",
            additionalComments: "",
            contactNumberOnChange: new EventEx(),
            contactNumberUpdate: new EventEx(),

            contactNameError: "",
            contactEmailError: "",
            phoneNumberError: "",
            companyNameError: "",
            shipToAddressError: "",
            pumpNumberError: "",
            needAccessoriesError: "",
            shippingMethodError: "",

            modalIsOpen: false,
            inProgress: false,
            shipToAddresses: [],
            billToId: props.session.billToId ? props.session.billToId : "",
            booleanDropdownOptions: [
                {
                    optionText: "No",
                    optionValue: "No",
                    searchString: "No",
                },
                {
                    optionText: "Yes",
                    optionValue: "Yes",
                    searchString: "Yes",
                },
            ],
            shippingMethodsOptions:
                props.shippingMethods !== undefined
                    ? props.shippingMethods.split("//").map<OptionObject>(value => ({
                        optionText: value,
                        optionValue: value,
                        searchString: value,
                    }))
                    : [],
        };

        this.state.contactNumberOnChange.subscribe(async (phoneNumberState: PhoneNumberState) => {
            await this.setState({ phoneNumber: phoneNumberState.value });
            await this.setState({ phoneNumberError: phoneNumberState.error });
        });

        this.getCompanyInformation = this.getCompanyInformation.bind(this);
        this.resetFormData = this.resetFormData.bind(this);
        this.modalCloseHandler = this.modalCloseHandler.bind(this);
        this.openModal = this.openModal.bind(this);
        this.checkForErrors = this.checkForErrors.bind(this);
        this.submitButtonClickHandler = this.submitButtonClickHandler.bind(this);
    }

    async getCompanyInformation() {
        if (!this.state.isAuthenticated) {
            return;
        }

        if (!this.state.session.billToId) {
            return;
        }

        const getBillToParams: GetBillToApiParameter = {
            billToId: this.state.billToId ? this.state.billToId : "",
            expand: ["shipTos"],
        };
        const billToModel = await getBillTo(getBillToParams);

        if (billToModel.shipTos) {
            const shippingAddresses = billToModel.shipTos?.filter(x => !x.isNew && !x.oneTimeAddress);
            const currentShipToAddresses = shippingAddresses.map<OptionObject>(x => ({
                optionText: x.fullAddress,
                optionValue: x.fullAddress,
                searchString: x.fullAddress,
            }));

            await this.setState({ shipToAddress: currentShipToAddresses[0]?.optionValue ?? "" });
            await this.setState({ shipToAddresses: currentShipToAddresses });
        }

        if (phoneRegex.test(billToModel.phone)) {
            await this.setState({ phoneNumber: billToModel.phone });
        }

        await this.setState({ companyName: billToModel.companyName });
    }

    async resetFormData() {
        await this.setState({
            contactName: this.state.isAuthenticated
                ? `${this.state.session.firstName} ${this.state.session.lastName}`
                : "",
            contactEmail: this.state.isAuthenticated ? this.state.session.email : "",
            companyName: "",
            shipToAddress: this.state.shipToAddresses[0]?.optionValue ?? "",
            accountNumber: "",
            poNumber: "",
            pumpNumber: 1,
            shippingMethod: this.state.shippingMethodsOptions[0]?.optionValue ?? "",
            rentalPricingBeforeShipping: "No",
            additionalComments: "",
            needAccessories: "No",

            contactNameError: "",
            contactEmailError: "",
            companyNameError: "",
            shipToAddressError: "",
            pumpNumberError: "",
            needAccessoriesError: "",
            shippingMethodError: "",

            inProgress: false,
        });
        await this.getCompanyInformation();
    }

    async modalCloseHandler() {
        await this.setState({ modalIsOpen: false });
    }

    async openModal() {
        await this.resetFormData();
        await this.setState({ modalIsOpen: true });
    }

    validatePONumber = async (poNumber: string) => {
        const requiredFieldMessage = translate("This is a required field.");
        if (!poNumber) {
            await this.setState({ poNumberError: requiredFieldMessage });
            return false;
        }
        await this.setState({ poNumberError: "" });
        return true;
    };        

    async checkForErrors() {
        const requiredFieldMessage = translate("This is a required field.");
        const emailFieldMessage = translate("Enter a valid email address.");
        const emailRegex = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
        let encounteredError = false;

        if (!emailRegex.test(this.state.contactEmail)) {
            await this.setState({ contactEmailError: emailFieldMessage });
            encounteredError = true;
        }

        if (!this.state.contactName || this.state.contactName === "") {
            await this.setState({ contactNameError: requiredFieldMessage });
            encounteredError = true;
        }

        if (!this.state.companyName || this.state.companyName === "") {
            await this.setState({ companyNameError: requiredFieldMessage });
            encounteredError = true;
        }

        if (!this.state.shipToAddress || this.state.shipToAddress === "") {
            await this.setState({ shipToAddressError: requiredFieldMessage });
            encounteredError = true;
        }

        if (!this.state.pumpNumber || this.state.pumpNumber < 1) {
            await this.setState({ pumpNumberError: requiredFieldMessage });
            encounteredError = true;
        }

        if (!this.state.shippingMethod || this.state.shippingMethod === "") {
            await this.setState({ shippingMethodError: requiredFieldMessage });
            encounteredError = true;
        }

        if (!this.state.needAccessories || this.state.needAccessories === "") {
            await this.setState({ needAccessoriesError: requiredFieldMessage });
            encounteredError = true;
        }

        await this.state.contactNumberUpdate.emitAsync();
        if (this.state.phoneNumberError) {
            encounteredError = true;
        }

        if (!this.state.noPORequired) {
            const poNumberValid = await this.validatePONumber(this.state.poNumber);
            if (!poNumberValid) {
                encounteredError = true;
            }
        }

        return encounteredError;
    }

    async submitButtonClickHandler(addToast: Function) {
        const successToast = {
            body: siteMessage("PumpRentalConfirmationMessage"),
            messageType: "info",
        };

        const dataHasErrors = await this.checkForErrors();

        if (dataHasErrors) {
            return;
        }

        await this.setState({ inProgress: true });

        const model: PumpRentalEmailRequest = {
            contactName: this.state.contactName,
            contactEmail: this.state.contactEmail,
            companyName: this.state.companyName,
            shipToAddress: this.state.shipToAddress,
            accountNumber: this.state.accountNumber,
            poNumber: this.state.poNumber,
            pumpNumber: this.state.pumpNumber,
            shippingMethod: this.state.shippingMethod,
            rentalPricingBeforeShipping: this.state.rentalPricingBeforeShipping,
            needAccessories: this.state.needAccessories,
            additionalComments: this.state.additionalComments,
            contactNumber: this.state.phoneNumber,
            emailTos: this.props.emailRecipients !== undefined ? this.props.emailRecipients.split("//") : [],
            productName: this.state.product.productTitle,
            productNumber: this.state.product.productNumber,
        };

        sendPumpRentalEmail(model);
        this.setState({ modalIsOpen: false });
        addToast(successToast);
    }

    render() {
        return (
            <>
                <Toaster>
                    <ToasterContext.Consumer>
                        {({ addToast }) => (
                            <>
                                <Link onClick={this.openModal} data-test-selector="pumpRentalLink">
                                    {translate("Rent Pump")}
                                </Link>
                                <Modal
                                    {...this.state.styles.modal}
                                    headline={this.state.title}
                                    isOpen={this.state.modalIsOpen}
                                    handleClose={this.modalCloseHandler}
                                >
                                    <GridContainer {...this.state.styles.container} data-test-selector="pumpRentalForm">
                                        <GridItem {...this.state.styles.oneLinerGridItem}>
                                            <div>
                                                <Typography>{this.state.description}</Typography>
                                            </div>
                                        </GridItem>

                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <TextField
                                                label={translate("Contact Name")}
                                                value={this.state.contactName}
                                                onChange={async e => {
                                                    await this.setState({ contactName: e.target.value });
                                                }}
                                                data-test-selector="pumpRentalContactName"
                                                error={this.state.contactNameError}
                                                required={true}
                                            />
                                        </GridItem>

                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <TextField
                                                label={translate("Contact Email")}
                                                value={this.state.contactEmail}
                                                onChange={async e => {
                                                    await this.setState({ contactEmail: e.target.value });
                                                }}
                                                data-test-selector="pumpRentalContactEmail"
                                                error={this.state.contactEmailError}
                                                required={true}
                                            />
                                        </GridItem>

                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <PhoneNumberField
                                                label={translate("Contact Number")}
                                                initialValue={this.state.phoneNumber}
                                                onChange={this.state.contactNumberOnChange}
                                                update={this.state.contactNumberUpdate}
                                                data-test-selector="pumpRentalContactNumber"
                                                format="000-000-0000"
                                                required={true}
                                                hideRequiredAsterisk={false}
                                            />
                                        </GridItem>

                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <TextField
                                                label={translate("Company Name")}
                                                value={this.state.companyName}
                                                onChange={async e => {
                                                    await this.setState({ companyName: e.target.value });
                                                }}
                                                data-test-selector="pumpRentalCompanyName"
                                                error={this.state.companyNameError}
                                                required={true}
                                            />
                                        </GridItem>

                                        <GridItem {...this.state.styles.oneLinerGridItem}>
                                            <DynamicDropdown
                                                label={translate("Ship To Address")}
                                                options={this.state.shipToAddresses}
                                                onSelectionChange={async value => {
                                                    if (value) {
                                                        await this.setState({ shipToAddress: value });
                                                    }
                                                }}
                                                data-test-selector="pumpRentalShipToAddress"
                                                selected={this.state.shipToAddress}
                                                error={this.state.shipToAddressError}
                                                required={true}
                                            />
                                        </GridItem>

                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <TextField
                                                label={translate("Account Number")}
                                                value={this.state.accountNumber}
                                                onChange={async e => {
                                                    await this.setState({ accountNumber: e.target.value });
                                                }}
                                                data-test-selector="pumpRentalAccountNumber"
                                                required={false}
                                            />
                                        </GridItem>

                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <TextField
                                                    label={translate("PO Number")}
                                                    value={this.state.poNumber}
                                                    onChange={async e => {
                                                        await this.setState({ poNumber: e.target.value });
                                                        if (!this.state.noPORequired) {
                                                            await this.validatePONumber(e.target.value);
                                                        } else {
                                                            await this.setState({ poNumberError: "" });
                                                        }
                                                    }}
                                                    data-test-selector="pumpRentalPoNumber"
                                                    required={!this.state.noPORequired}
                                                    error={this.state.poNumberError}
                                                />
                                                <div style={{ marginTop: '10px' }}>
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            checked={this.state.noPORequired}
                                                            onChange={async e => {
                                                                await this.setState({ noPORequired: e.target.checked });
                                                                if (e.target.checked) {
                                                                    await this.setState({ poNumberError: "" });
                                                                }
                                                            }}
                                                        />
                                                        {translate("No PO Required for Invoicing")}
                                                    </label>
                                                </div>
                                            </div>
                                        </GridItem>

                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <TextField
                                                label={translate("# Of Pumps Needed")}
                                                value={this.state.pumpNumber}
                                                type="number"
                                                min={1}
                                                onChange={async e => {
                                                    await this.setState({
                                                        pumpNumber: parseInt(e.target.value.replace(/\D/, ""), 10),
                                                    });
                                                }}
                                                data-test-selector="pumpRentalPumpNumber"
                                                error={this.state.pumpNumberError}
                                                required={true}
                                            />
                                        </GridItem>

                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <DynamicDropdown
                                                label={translate("Do You Need Accessories?")}
                                                options={this.state.booleanDropdownOptions}
                                                onSelectionChange={async value => {
                                                    if (value) {
                                                        await this.setState({ needAccessories: value });
                                                    }
                                                }}
                                                data-test-selector="pumpRentalNeedAccessories"
                                                selected={this.state.needAccessories}
                                                error={this.state.needAccessoriesError}
                                                required={true}
                                            />
                                        </GridItem>

                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <DynamicDropdown
                                                label={translate("Shipping Method")}
                                                options={this.state.shippingMethodsOptions}
                                                onSelectionChange={async value => {
                                                    if (value) {
                                                        await this.setState({ shippingMethod: value });
                                                    }
                                                }}
                                                data-test-selector="pumpRentalShippingMethod"
                                                selected={this.state.shippingMethod}
                                                error={this.state.shippingMethodError}
                                                required={true}
                                            />
                                        </GridItem>

                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <DynamicDropdown
                                                label={translate("Get rental pricing before shipping?")}
                                                options={this.state.booleanDropdownOptions}
                                                onSelectionChange={async value => {
                                                    if (value) {
                                                        await this.setState({ rentalPricingBeforeShipping: value });
                                                    }
                                                }}
                                                data-test-selector="pumpRentalRentalPricingBeforeShipping"
                                                selected={this.state.rentalPricingBeforeShipping}
                                                required={false}
                                            />
                                        </GridItem>

                                        <GridItem {...this.state.styles.oneLinerGridItem}>
                                            <TextArea
                                                label={translate("Additional Comments")}
                                                value={this.state.additionalComments}
                                                onChange={async e => {
                                                    await this.setState({ additionalComments: e.target.value });
                                                }}
                                                data-test-selector="productPriceAdditionalComments"
                                            />
                                        </GridItem>

                                        <GridItem
                                            {...this.state.styles.oneLinerGridItem}
                                        >{`* ${this.state.requiredFieldsLabel}`}</GridItem>

                                        <GridItem
                                            {...this.state.styles.oneLinerGridItem}
                                        >{`** ${this.state.additionalChargesLabel}`}</GridItem>

                                        <GridItem {...this.state.styles.oneLinerGridItem}>
                                            <Button
                                                {...this.state.styles.submitButton}
                                                onClick={() => this.submitButtonClickHandler(addToast)}
                                                data-test-selector="pumpRentalSubmit"
                                                disabled={this.state.inProgress}
                                            >
                                                {translate("Submit")}
                                            </Button>
                                        </GridItem>
                                    </GridContainer>
                                </Modal>
                            </>
                        )}
                    </ToasterContext.Consumer>
                </Toaster>
            </>
        );
    }
}

export default connect(mapStateToProps)(withProduct(PumpRentalModal));
